import {afterNextRender, Component, computed, DestroyRef, ElementRef, inject, input, OnDestroy, OnInit} from '@angular/core';
import {PrintService} from '@core/services/print.service';
import {WineProduct} from '@core/interfaces/generated/graphql';
import {StoryblokService} from '@core/services/storyblok.service';
import {ProductService} from '@core/services/product.service';
import {RoutingService} from '@core/services/routing.service';
import {NgOptimizedImage} from '@angular/common';
import {RatingToStarsPipe} from '@core/pipes/rating-to-stars.pipe';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {faCircle} from '@fortawesome/free-solid-svg-icons';
import {ProductTagsComponent} from '@layout/components/product/product-tags/product-tags.component';
import {environment} from '@env/environment';
import {
  ProductDescriptionTextComponent
} from '@modules/page/components/product/components/product-description-section/product-description-text/product-description-text.component';
import {SafeHtmlPipe} from '@core/pipes/safe-html.pipe';


@Component({
  selector: 'app-print-pdf',
  templateUrl: './print-pdf.component.html',
  styleUrls: ['./print-pdf.component.scss'],
  imports: [RatingToStarsPipe, NgOptimizedImage, FaIconComponent, ProductTagsComponent, ProductDescriptionTextComponent, SafeHtmlPipe]
})
export class PrintPdfComponent implements OnInit, OnDestroy {
  storyblokService = inject(StoryblokService);
  productService = inject(ProductService);
  protected routingService = inject(RoutingService);
  private printService = inject(PrintService);

  constructor() {
    const el = inject(ElementRef);
    const destroyRef = inject(DestroyRef);
    afterNextRender(() => {
      const onAfterPrint = () => this.printService.setIsPrinting(false);
      window.addEventListener('afterprint', onAfterPrint, {once: true});
      destroyRef.onDestroy(() => window.removeEventListener('afterprint', onAfterPrint));

      const images = Array.from(el.nativeElement.querySelectorAll('img')) as HTMLImageElement[];
      const loadPromises = images.map(img => new Promise<void>(resolve => {
        if (img.complete) {
          resolve();
          return;
        }
        const onLoad = () => {
          img.removeEventListener('load', onLoad);
          img.removeEventListener('error', onError);
          resolve();
        };
        const onError = () => {
          img.removeEventListener('load', onLoad);
          img.removeEventListener('error', onError);
          console.warn('[PrintPdfComponent] Image failed to load:', img.src);
          resolve();
        };
        img.addEventListener('load', onLoad, {once: true});
        img.addEventListener('error', onError, {once: true});
        // Guard against completing between the check and listener registration
        if (img.complete) {
          onLoad();
        }
      }));

      Promise.all(loadPromises).then(() => window.print());
    });
  }


  productAttributes: any[] = [];

  product = input<WineProduct | undefined>();

  baPercentage = input<string | null>(null);

  foods = computed(() => {
    return this.product()?.meta?.foods?.filter((food) => food?.pdfIcon).map((food) => {
      if (!food?.pdfIcon) {
        return food;
      }
      food.pdfIcon = environment.supervin.assets + food?.pdfIcon;
      return food;
    })
  })

  ngOnInit(): void {
    this.getProductAttributes();
  }

  ngOnDestroy() {
    this.printService.setIsPrinting(false);
  }

  prepareReviewRating(reviewRating: number | undefined | null, reviewLabel: string | undefined | null) {
    if (reviewRating) {
      return reviewRating + ' ' + $localize`Point`;
    }
    return reviewLabel;
  }

  protected readonly faCircle = faCircle;

  getProductAttributes() {
    const product = this.product();
    if (!product) {
      return;
    }

    if (product.meta?.countryEnt?.title) {
      this.productAttributes.push({label: $localize`Land:`, value: product.meta.countryEnt.title});
    }

    if (product.meta?.areaEnt?.title) {
      this.productAttributes.push({label: $localize`Område:`, value: product.meta.areaEnt.title});
    }

    if (product.meta?.districtEnt?.title) {
      this.productAttributes.push({label: $localize`Distrikt:`, value: product.meta.districtEnt?.title});
    }

    if ((product.meta?.grapesV2?.length ?? 0) > 0) {
      this.productAttributes.push({
        label: $localize`Drue:`,
        value: product.meta?.grapesV2?.map(grape => grape?.title).join(', ')
      });
    }
    if (product.meta?.serving) {
      this.productAttributes.push({label: $localize`Servering:`, value: product.meta.serving});
    }
    if (product.meta?.savingPotential) {
      this.productAttributes.push({label: $localize`Gemmepotientiale:`, value: product.meta.savingPotential});
    }
    if (product.meta?.alcohol) {
      const value = product.meta.alcohol;
      this.productAttributes.push({label: $localize`Alkohol:`, value: value.includes('%') ? value : value + ' %'});
    }
    if (product.meta?.contents) {
      this.productAttributes.push({label: $localize`Indhold:`, value: product.meta.contentLabel});
    }
  }

  getNumberOfBaPercentageCircles(): number[] {
    const baPercentage = parseInt(this.baPercentage() ?? '', 10);
    if (!baPercentage) {
      return [];
    }
    if (baPercentage < 3000) {
      return [1];
    }
    if (baPercentage >= 3000 && baPercentage < 3500) {
      return [1, 2];
    }
    if (baPercentage >= 3500) {
      return [1, 2, 3];
    }
    return [];
  }

}
